import { Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Page } from '@bloomreach/spa-sdk';
import { InsightsService } from '@marketing/insights/insights.service';
import { FtInsightsItem } from '@search/interfaces/insights.interface';
import {
  AppStateService,
  ProfileService,
  SiteConfigService,
  WidenService,
} from '@services';
import { LinkService } from '@services/link.service';
import { Link } from '@types';
import { HEADER_LOGO } from '@utils/app.constants';
import { convertCsvFields } from '@utils/data/object-utils';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ArticleContent, Contributor } from '../article.types';

/**
 * The Article Service contains some of the complex business logic that is not required
 * in the component.
 */
@Injectable({
  providedIn: 'root',
})
export class ArticleService {
  private unsubscribe$: Subject<void> = new Subject<void>();

  // TODO: Move complex business logic out of article component and into this service
  constructor(
    private appStateService: AppStateService,
    private ctaLinkService: LinkService,
    private meta: Meta,
    private pageTitle: Title,
    private profileService: ProfileService,
    private siteConfig: SiteConfigService,
    private widenService: WidenService,
    private insightsService: InsightsService
  ) {}

  audienceWidenAsset(audience: string): boolean {
    if (audience !== undefined) {
      const audienceList = audience.split(',');
      const segmentId = this.siteConfig.segmentId;
      for (const audi of audienceList) {
        if (audi === segmentId) {
          return true;
        } else if (
          audi === 'advisor' &&
          segmentId === 'financial-professionals'
        ) {
          return true;
        } else {
          return false;
        }
      }
    }
  }

  /**
   * Get Article Date from document object
   */
  getArticleDate(documentRef: string, page: Page): string {
    const relatedArticle = page
      .getContent(documentRef)
      ?.getData<ArticleContent>();
    return relatedArticle?.isodate;
  }

  // Set meta tags
  setMetaTags(metadata: any, imageUrl: string, title: string): void {
    if (metadata) {
      if (!metadata.title) {
        this.pageTitle.setTitle(
          title +
            (metadata.appendedTitleText
              ? ' | ' + metadata.appendedTitleText
              : '')
        );
      }
      if (!metadata.socialImage) {
        this.meta.updateTag({
          property: 'og:image',
          content: imageUrl,
        });
      }
      this.meta.updateTag({
        property: 'twitter:card',
        content: 'summary_large_image',
      });
      this.meta.addTags([
        {
          property: 'twitter:title',
          content: metadata.title,
        },
        {
          property: 'twitter:description',
          content: metadata.description,
        },
        {
          property: 'twitter:image',
          content: imageUrl,
        },
      ]);
    }
  }

  getSocialMediaImg(article: ArticleContent): string {
    let imageUrl = '';
    if (article?.image?.widenDocument) {
      imageUrl = this.widenService.getWidenImageVariantUrl(
        this.widenService.getWidenAssetUrl(article.image?.widenDocument),
        'original',
        'jpeg'
      );
    } else {
      imageUrl = HEADER_LOGO;
    }
    return imageUrl;
  }

  // Format authors
  formatAuthors(article: ArticleContent, page: Page): void {
    article.authors = [];
    for (const contributor of article.insightMetadata.contributors) {
      const contributorData = page
        .getContent(contributor)
        .getData<Contributor>();
      if (contributorData?.fullName) {
        const authorName = contributorData?.fullName;
        article.authors?.push(authorName);
      }
    }
  }

  // Format contributors
  formatContributors(article: ArticleContent, page: Page): void {
    article.contributors = [];
    for (const contributor of article.insightMetadata.contributors) {
      let contributorData: Contributor;
      contributorData = page.getContent(contributor).getData<Contributor>();
      if (contributorData?.profileImage) {
        contributorData.profileImageUrl = this.widenService.getWidenImageVariantUrl(
          this.widenService.getWidenAssetUrl(
            contributorData.profileImage?.widenAsset
          ),
          'original',
          'webp'
        );
        if (contributorData.profileImageUrl === 'undefined') {
          contributorData.profileImageUrl = '/assets/images/blank-spacer.png';
        }
        // Adding p tag if p tag doesnot exists on api
        if (contributorData?.overview) {
          if (!contributorData?.overview.includes('<p>')) {
            contributorData.overview =
              '<p>' + contributorData?.overview + '</p>';
          }
        }
      } else {
        contributorData.profileImageUrl = '/assets/images/blank-spacer.png';
      }
      article.contributors?.push(contributorData);
    }
  }

  /**
   * Format the metadata
   * @param article R
   */
  formatMetadata(article: ArticleContent): void {
    if (article?.insightMetadata?.investmentteam) {
      article.investmentteam = article?.insightMetadata?.investmentteam;
    }

    if (article?.insightMetadata?.investmenttheme) {
      article.investmenttheme = article?.insightMetadata?.investmenttheme;
    }

    if (article?.insightMetadata?.assetclass) {
      article.assetclassPage = article?.insightMetadata?.assetclass;
    }

    if (article?.insightMetadata?.speciality) {
      article.speciality = article?.insightMetadata?.speciality;
    }

    if (article?.insightMetadata?.publicationseries) {
      article.publicationseries = article?.insightMetadata?.publicationseries;
    }

    if (article?.insightMetadata?.audienceExclusions) {
      article.audienceExclusions = article?.insightMetadata?.audienceExclusions;
    }
  }

  // Related Content
  getRelatedContent(
    segmentTags: string,
    article: ArticleContent,
    page: Page
  ): void {
    for (const relatedContent of article.relatedContent) {
      if (relatedContent.localizableLink.length) {
        for (const localizeLink of relatedContent.localizableLink) {
          if (localizeLink.targeting) {
            localizeLink.isAllowed = this.isSegmentTagAvailable(
              segmentTags,
              localizeLink.targeting
            );
          } else {
            localizeLink.isAllowed = true;
          }
          if (localizeLink?.link?.linkCollection?.length) {
            const linkBlock: Link = localizeLink?.link?.linkCollection[0];
            localizeLink.link.displayText = linkBlock.displayText;
            localizeLink.link.target = linkBlock.target;
            localizeLink.link.external = linkBlock.external;
            localizeLink.link.externalSuffix = linkBlock.externalSuffix;
            localizeLink.link.title = linkBlock.title;
            localizeLink.link.url = this.ctaLinkService.getCTALink(
              page,
              linkBlock,
              this.appStateService?.getHomePageUrl()
            );
            if (linkBlock?.document) {
              localizeLink.link.date = this.getArticleDate(
                linkBlock.document,
                page
              );
            }
            if (linkBlock?.literatureCode) {
              const spaBaseURl = this.appStateService?.getCmsBaseUrl() + '/';
              localizeLink.link.url =
                spaBaseURl +
                this.siteConfig.literature.litDetailUrl +
                '/' +
                linkBlock.literatureCode;
            }
          }
          if (localizeLink.isodate) {
            localizeLink.isodate = this.insightsService.getLongDate(
              localizeLink.isodate
            );
          }
        }
      }
      if (relatedContent.dynamicFilters) {
        this.insightsService
          .getAricleAPIData$(
            relatedContent.dynamicFilters?.articleFilters,
            relatedContent.dynamicFilters?.maxResults
          )
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe((response: any) => {
            const results = response.results.response.hits.hits;
            results.forEach((element: FtInsightsItem) => {
              let articleUrl: string;
              if (element._source?.relativePath?.startsWith('/')) {
                articleUrl =
                  this.appStateService?.getSpaBaseUrl() +
                  element._source?.relativePath;
              } else {
                articleUrl =
                  this.appStateService?.getSpaBaseUrl() +
                  '/' +
                  element._source?.relativePath;
              }
              relatedContent.localizableLink.push({
                isAllowed: true,
                isodate: this.insightsService.getLongDate(
                  element._source?.referenceDate
                ),
                link: {
                  displayText: this.replaceSpecialCharacters(
                    element._source?.title
                  ),
                  target: '',
                  external: false,
                  title: element._source?.title,
                  url: articleUrl,
                },
              });
            });
            relatedContent.localizableLink.forEach((element) => {
              if (element.isAllowed) {
                relatedContent.showHeading = true;
              }
            });
          });
      }
      relatedContent.localizableLink.forEach((element) => {
        if (element.isAllowed) {
          relatedContent.showHeading = true;
        }
      });
    }
  }

  /**
   * Replaces HTML special character codes
   * @param input segment tag stringraw string
   * @returns formatted string
   */
  replaceSpecialCharacters(input: string): string {
    const specialChars: {
      htmlChars: string;
      replacedValue: string;
    }[] = [
      {
        htmlChars: '&rsquo;',
        replacedValue: '’',
      },
    ];

    for (const specialChar of specialChars) {
      const regex = new RegExp(specialChar.htmlChars, 'g');
      input = input.replace(regex, specialChar.replacedValue);
    }

    return input;
  }

  /**
   * Set Local pdf (Moved from component)
   * @param segmentTags segment tag string
   * @param article articleContent object
   */
  setLocalizablePdf(segmentTags: string, article: ArticleContent): void {
    article.pdf = [];
    article.localizablePdf.forEach((localizePdf) => {
      if (localizePdf.targeting) {
        localizePdf.isAllowed = this.isSegmentTagAvailable(
          segmentTags,
          localizePdf.targeting
        );
      } else {
        localizePdf.isAllowed = true;
      }

      if (localizePdf.pdf.target === '_blank') {
        localizePdf.pdf.isNewWindow = true;
      }

      const widenAsset = localizePdf.pdf.widenAssetCompound.widenDocument;
      const litCode = this.widenService.getLitCode(widenAsset);
      const isClientUse = this.widenService.getIsClientUse(widenAsset);
      const audience = this.audienceWidenAsset(
        this.widenService.getwidenAssetAudience(widenAsset)
      );
      if (audience && isClientUse === 'Y') {
        localizePdf.pdf.href = this.widenService.getWidenAssetUrl(widenAsset);
      } else if (
        audience &&
        !this.profileService.isLoggedIn() &&
        litCode !== undefined
      ) {
        const spaBaseURl = this.appStateService?.getCmsBaseUrl() + '/';
        localizePdf.pdf.href =
          spaBaseURl + this.siteConfig.literature.litDetailUrl + '/' + litCode;
      } else {
        localizePdf.pdf.href = this.widenService.getWidenAssetUrl(widenAsset);
      }
      if (localizePdf.isAllowed) {
        article.pdf.push(localizePdf.pdf);
      }
    });
  }

  /**
   * Check if tags are matching with site config.
   * (Moved from component)
   */
  isSegmentTagAvailable(segmentTags: string, target: string): boolean {
    if (target) {
      const parsedValue = JSON.parse(target);
      const documentAudience = convertCsvFields(
        parsedValue?.insightAudiences_csv
      );
      const siteAudience = segmentTags?.split(',');
      const includeAudience = siteAudience?.filter((audience) =>
        documentAudience?.includes(audience)
      );
      return !!includeAudience?.length;
    }
  }
}
